import { ProjectConsumer } from '../../core/providers/ProjectProvider';
import { hotkeys } from 'react-keyboard-shortcuts';
import { v4 as uuidv4 } from 'uuid';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import ActionText from '../../core/ActionText';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import PagingFilter from '../../core/PagingFilter';
import ProjectLanguageSelector from '../../core/ProjectLanguageSelector';
import { globalStyles } from '../../core/style/gstheme';
import CreateTopConceptDialogContainer from './CreateTopConceptDialogContainer';
import { makeCancelable, sortResourcesWithFallback } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import ConceptData from './ConceptData';
import ConceptStructure from './ConceptStructure';

const styles = Object.assign({}, globalStyles);

class ConceptView extends Component {

  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      initialSearch: true,
      concepts: undefined,
      text: "",
      takingTooLong: false,
      searchID: null,
      page: 1,
      noneFound: false,
      noResults: true,
      filterUnused: false,
      isErrored: null
    };
    this.infoRef = React.createRef();
    this.controlRef = React.createRef();
    this.cancellable = null;
  }

  componentDidMount() {
    this.handlePageChange(1, true);
  }

  hot_keys = {
    'alt+i': {
      priority: 1,
      handler: () => {
        if (this.infoRef && this.infoRef.current) {
          this.infoRef.current.focus();
        }
      }
    },
    'alt+c': {
      priority: 1,
      handler: () => {
        if (this.controlRef && this.controlRef.current) {
          this.controlRef.current.focus();
        }
      }
    }
  };

  static getDerivedStateFromProps(props, state) {
    if (props.viewLang !== state.viewLang) {
      return {
        isLoading: false,
        initialSearch: state.text === "",
        viewLang: props.viewLang,
        concepts: undefined,
        updateAt: undefined,
        noneFound: false,
        noResults: true
      };
    }
    return null;
  }

  componentDidUpdate() {
    if (!this.state.isLoading && this.state.concepts === undefined && this.state.noResults === true) {
      this.handlePageChange(this.state.page, this.state.initialSearch);
    }
  }


  handlePageRefresh = () => {
    this.setState({ text: "", initialSearch: true }, () => this.handlePageChange(this.state.page));
  };

  handleFilterChange = (text) => {
    this.setState({ text, initialSearch: text === "" }, () => this.handlePageChange(this.state.page, text === ""));
  };

  handleChange = name => event => {
    this.setState({ [name]: event.target.checked }, () => this.handlePageChange(this.state.page, this.state.text === ""));
  };

  handlePageChange = async (page = 1, initialSearch = false) => {
    if ((!initialSearch || (this.state.takingTooLong && this.state.filterUnused)) && this.state.text.length < 1) return;
    this.setState(
      { isLoading: true, takingTooLong: false },
      () => {
        let search = [
          {
            predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
            value: "http://www.w3.org/2004/02/skos/core#Concept"
          }
        ];
        if (this.props.flat) {
          search.push({
            predicate: "http://www.w3.org/2004/02/skos/core#hasTopConcept",
            value: this.props.schemeIRI,
            reverse: true
          });
        }
        else {
          search.push({
            predicate: "http://www.w3.org/2004/02/skos/core#inScheme",
            value: this.props.schemeIRI
          });
        }
        if (this.state.text !== "") {
          search.push({
            predicate: ["http://www.w3.org/2004/02/skos/core#prefLabel", "http://www.w3.org/2004/02/skos/core#altLabel"],
            value: this.state.text,
            isExactMatch: false
          });
        }
        const searchID = uuidv4();
        this.setState({ searchID }, () => {
          this.doSearch({
            search: search,
            noCount: true,
            retainOrder: initialSearch,
            ...(initialSearch) && { fast: true },
            ...(this.state.filterUnused) && {
              excludeByPath: [
                {
                  predicate: [
                    "http://www.w3.org/2004/02/skos/core#topConceptOf"
                  ],
                  target: this.props.schemeIRI,
                },
                {
                  predicate: [
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#topConceptOf"
                  ],
                  target: this.props.schemeIRI,
                },
                {
                  predicate: [
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#topConceptOf"
                  ],
                  target: this.props.schemeIRI,
                },
                {
                  predicate: [
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#topConceptOf"
                  ],
                  target: this.props.schemeIRI,
                },
                {
                  predicate: [
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#topConceptOf"
                  ],
                  target: this.props.schemeIRI,
                },
                {
                  predicate: [
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#topConceptOf"
                  ],
                  target: this.props.schemeIRI,
                },
                {
                  predicate: [
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#topConceptOf"
                  ],
                  target: this.props.schemeIRI,
                },
                {
                  predicate: [
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#topConceptOf"
                  ],
                  target: this.props.schemeIRI,
                },
                {
                  predicate: [
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#topConceptOf"
                  ],
                  target: this.props.schemeIRI,
                },
                {
                  predicate: [
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#topConceptOf"
                  ],
                  target: this.props.schemeIRI,
                },
                {
                  predicate: [
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#topConceptOf"
                  ],
                  target: this.props.schemeIRI,
                },
                {
                  predicate: [
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#topConceptOf"
                  ],
                  target: this.props.schemeIRI,
                },
                {
                  predicate: [
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#topConceptOf"
                  ],
                  target: this.props.schemeIRI,
                },
                {
                  predicate: [
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#topConceptOf"
                  ],
                  target: this.props.schemeIRI,
                },
                {
                  predicate: [
                    "http://www.w3.org/2004/02/skos/core#broader",
                    "http://www.w3.org/2004/02/skos/core#topConceptOf"
                  ],
                  target: this.props.schemeIRI,
                  pathOperators: [
                    "*"
                  ]
                }
              ]
            },
            graph: [this.props.graph],
            exclude: this.props.deletedConcepts,
            ...(this.state.filterUnused) && {
              excludeByPathOnly: true
            },
            entailment: this.props.skosXL ? ["propertyChain"] : [],
            predicates: ["http://www.w3.org/2004/02/skos/core#prefLabel"],
            page: page,
            language: this.props.viewLang,
            pageSize: 200,
            sort: ["http://www.w3.org/2004/02/skos/core#prefLabel"]
          }, searchID)
            .promise
            .then(data => {
              // Ignore 'old' 
              if (this.cancellable && this.cancellable.isCancelled()) return;
              if (data.uuid && data.uuid !== this.state.searchID) return;
              if (data.status) {
                this.setState({
                  takingTooLong: true,
                  isLoading: false,
                  page: 1,
                  noneFound: true,
                  noResults: false
                });
                return;
              }
              if (data.results && data.results.length > 0) {
                const conceptIDs = data.results.map(result => result.id);
                this.loadData(conceptIDs, {})
                  .promise
                  .then(() => {
                    GS.handle("concept-search");
                    if (this.cancellable && this.cancellable.isCancelled()) return;
                    // Loading might take a while so check we haven't done another search in the meantime
                    if (data.uuid && data.uuid !== this.state.searchID) return;
                    const concepts = sortResourcesWithFallback(conceptIDs, this.props.graph, "prefLabel", this.props.viewLang, this.props.defaultLang);
                    this.setState({
                      concepts,
                      page,
                      noResults: false,
                      noneFound: false,
                      isLoading: false
                    });
                  })
                  .catch((e) => {
                    if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
                  });
              }
              else {
                if (this.cancellable && this.cancellable.isCancelled()) return;
                this.setState({
                  isLoading: false,
                  page: 1,
                  noneFound: true,
                  noResults: false
                });
              }
            })
            .catch((e) => {
              if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
            });
        });
      });
  };

  doSearch(payload, uuid) {
    this.cancellable = makeCancelable(GS.search(payload, uuid, [500]));
    return this.cancellable;
  }

  loadData(iris, options) {
    this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph, true));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  render() {

    const { isLoading, concepts, text, isErrored, noneFound, takingTooLong } = this.state;
    const { activeConcept, data, onSelect, defaultLang, viewLang, onSelectViewLanguage, projectUsers, ontologyProjects,
      showLabelDetail, showFloatingLabel, defaultClasses, iriOptions, useSlashIRI, useUuidIRI, onFoundConcept, flat,
      onAddNewConcept, onAddExistingConcept, titlePath, addToScheme, useCounterIRI, counters,
      onDeleteConcept, classes, schemeIRI, appBar, isReadOnly, graph, project, entitlements, hiddenProps } = this.props;

    if (isErrored) return <Errored data={isErrored} />;

    return (
      <ProjectConsumer>
        {({ largeDataSettings }) => (
          <div>
            <div className={classes.controlPanel}>
              <div className={classes.views}>
                {appBar}
              </div>
              <div className={classes.controlPanelInner}>
                {flat &&
                  <div data-test="add-top-concept" className={classes.controlPanelAdd}>
                    {!isReadOnly &&
                      <CreateTopConceptDialogContainer
                        schemeIRI={schemeIRI}
                        graph={graph}
                        useXL={data.useXL}
                        useWorkflow={data.activateWorkflow === true}
                        iriOptions={iriOptions}
                        useSlashIRI={useSlashIRI}
                        useUuidIRI={useUuidIRI}
                        useCounterIRI={useCounterIRI}
                        counters={counters}
                        onAddExistingConcept={onAddExistingConcept(titlePath, "")}
                        onAddNewConcept={onAddNewConcept(titlePath, "")}
                      />
                    }
                  </div>
                }
                <div className={classes.controlPanelSearch} style={{ marginLeft: flat && !isReadOnly ? 2 : 30 }}>
                  <PagingFilter
                    autoFocus={true}
                    handleFilterChange={this.handleFilterChange}
                    text={text}
                    maxLength={50}
                    placeholder="Max 200 displayed. Filter to refine." />
                </div>
              </div>
              <div tabIndex={0} ref={this.controlRef} className={classes.tree}>
                {!flat && !largeDataSettings &&
                  <div className={classes.treeOptions}>
                    <FormControlLabel
                      classes={{ label: classes.formLabelInline }}
                      control={
                        <Switch
                          data-test="unused-concepts"
                          checked={this.state.filterUnused}
                          onChange={this.handleChange('filterUnused')}
                          value="filterUnused"
                          color="primary"
                        />
                      }
                      label="Unconnected Concepts Only"
                    />
                  </div>
                }
                {
                  isLoading ?
                    <Loading offset="100px" message={text === "" ? <span>If this takes more than a few seconds<br />try filtering.</span> : null} />
                    :
                    takingTooLong ?
                      <ActionText format="result" dataTest="too-long" offset="100px"><span>We're struggling to get you some results.<br />Try filtering.</span></ActionText>
                      :
                      <ConceptStructure
                        concepts={concepts}
                        showInScheme={true}
                        schemeIRI={schemeIRI}
                        iriOptions={iriOptions}
                        addToScheme={addToScheme}
                        graph={graph}
                        flat={flat}
                        useWorkflow={data.activateWorkflow === true}
                        noneFound={noneFound}
                        activeConcept={activeConcept}
                        defaultLang={defaultLang}
                        viewLang={viewLang}
                        isReadOnly={isReadOnly}
                        onSelect={onSelect} />
                }
              </div>
            </div>
            <div className={classes.viewLanguagePanel}>
              <ProjectLanguageSelector selectedLang={viewLang} includeLabel={false} onSelect={onSelectViewLanguage} />
            </div>
            <div data-test="info-panel" ref={this.infoRef} tabIndex={0} className={classes.infoPanel}>
              {activeConcept === null ?
                <ActionText offset="180px">Select a concept to view the detail.</ActionText>
                :
                <ConceptData
                  key={activeConcept}
                  handleNotAvailable={true}
                  id={activeConcept}
                  flat={flat}
                  annotate={data.activateAnnotate === true}
                  associatedProject={data.associatedProject ?
                    Array.isArray(data.associatedProject) ?
                      data.associatedProject
                      :
                      [data.associatedProject]
                    :
                    []
                  }
                  entitlements={entitlements}
                  projectUsers={projectUsers}
                  enableTSV={true}
                  iriOptions={iriOptions}
                  schemeIRI={schemeIRI}
                  viewLang={viewLang}
                  ontologyProjects={ontologyProjects}
                  onFoundConcept={onFoundConcept}
                  hiddenProps={hiddenProps}
                  linkedData={data.linkedData}
                  propertyTemplates={data.associatedTemplate}
                  defaultClasses={defaultClasses}
                  graph={graph}
                  useSlashIRI={useSlashIRI}
                  useWorkflow={data.activateWorkflow === true}
                  useUuidIRI={useUuidIRI}
                  useCounterIRI={useCounterIRI}
                  counters={counters}
                  project={project}
                  resourcesForNotes={data.resourcesForNotes}
                  showFloatingLabel={showFloatingLabel}
                  showLabelDetail={showLabelDetail}
                  conceptIRI={activeConcept}
                  onDeleteConcept={onDeleteConcept} />
              }
            </div>
          </div>
        )}
      </ProjectConsumer>
    );
  }

}

ConceptView.propTypes = {
  appBar: PropTypes.node.isRequired,
  entitlements: PropTypes.array.isRequired,
  graph: PropTypes.string.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  skosXL: PropTypes.bool.isRequired,
  onAddExistingConcept: PropTypes.func.isRequired,
  onAddNewConcept: PropTypes.func.isRequired,
  flat: PropTypes.bool,
  useXL: PropTypes.bool,
  useSlashIRI: PropTypes.bool,
  useUuidIRI: PropTypes.bool,
  useCounterIRI: PropTypes.bool,
  counters: PropTypes.object,
  defaultClasses: PropTypes.array,
  addToScheme: PropTypes.func,
  hiddenProps: PropTypes.array,
  schemeIRI: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  project: PropTypes.string.isRequired,
  onSelectViewLanguage: PropTypes.func.isRequired,
  updateAt: PropTypes.number,
  projectUsers: PropTypes.array,
  titlePath: PropTypes.array,
  activeConcept: PropTypes.string,
  showFloatingLabel: PropTypes.func,
  onFoundConcept: PropTypes.func,
  showLabelDetail: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
  ontologyProjects: PropTypes.array,
  onDeleteConcept: PropTypes.func.isRequired,
  defaultLang: PropTypes.string.isRequired,
  viewLang: PropTypes.string,
  deletedConcepts: PropTypes.arrayOf(PropTypes.string).isRequired,
  classes: PropTypes.object,
  iriOptions: PropTypes.object
};

export default withStyles(styles)(hotkeys(ConceptView));